import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const AuthContext = createContext(undefined);

const AUTH_TIMEOUT_MS = 5000;

async function ensureProfile(authUser) {
  const { data: profile, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("user_id", authUser.id)
    .maybeSingle();

  if (profile) return profile;

  // First-time profile creation — pull phone metadata if present
  // (set during email signup or by CompleteProfile after Google).
  const meta = authUser.user_metadata || {};
  const insertPayload = { user_id: authUser.id, role: "user" };
  if (meta.phone) insertPayload.phone = meta.phone;
  if (meta.phone_country_code) insertPayload.phone_country_code = meta.phone_country_code;

  const { data: created } = await supabase
    .from("profiles")
    .upsert(insertPayload, { onConflict: "user_id" })
    .select()
    .single();

  return created;
}

function buildUser(authUser, profile) {
  const meta = authUser.user_metadata || {};
  const realEmail = meta.real_email || authUser.email;
  return {
    ...(profile || {}),
    id: authUser.id,
    // Academy-scoped auth users store an internal routing email in Auth.
    // Keep the UI-facing email/name based on the real email metadata while
    // preserving raw metadata for academy isolation checks in useStudentAuth.
    email: realEmail,
    auth_email: authUser.email,
    full_name: meta.full_name || meta.name || realEmail?.split("@")[0],
    user_metadata: meta,
    app_metadata: authUser.app_metadata || {},
    user_id: authUser.id,
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState({ id: 'dwrrah' });
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const initialLoadDone = useRef(false);

  // Admin-driven impersonation flag — true whenever this tab landed via an
  // adminImpersonate{Student,Instructor} magic-link OR is reusing a
  // persisted impersonation session. Hooks/guards consume this to bypass
  // student-side blocks (suspended/owner-as-student/needsRegistration) and
  // instructor-side plan locks, because the admin is operating *as* the
  // impersonated user with full intent.
  const computeImpersonating = () => {
    if (typeof window === "undefined") return false;
    try {
      if (new URLSearchParams(window.location.search).get("admin_impersonation") === "1") return true;
      return !!sessionStorage.getItem("admin_impersonation");
    } catch { return false; }
  };
  const [isAdminImpersonating, setIsAdminImpersonating] = useState(computeImpersonating());
  useEffect(() => {
    // Re-check on mount and on storage events (banner clears it on exit).
    setIsAdminImpersonating(computeImpersonating());
    const onStorage = (e) => {
      if (e.key === "admin_impersonation") setIsAdminImpersonating(computeImpersonating());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    setAuthTimedOut(false);
    // Soft timeout: surface a non-destructive flag the consumer can react to.
    // Do NOT flip isLoadingAuth=false — that would cause hooks to assume "no session"
    // and force-redirect a user whose session is still resolving.
    const timeout = setTimeout(() => {
      if (!initialLoadDone.current) {
        console.warn("Auth loading timed out after", AUTH_TIMEOUT_MS, "ms — surfacing retry");
        setAuthTimedOut(true);
      }
    }, AUTH_TIMEOUT_MS);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Detect PASSWORD_RECOVERY event regardless of init state
      if (event === "PASSWORD_RECOVERY") {
        setIsRecoveryMode(true);
      }

      // Skip INITIAL_SESSION — handled by checkAppState
      if (!initialLoadDone.current) return;

      if (session?.user) {
        setIsAuthenticated(true);
        ensureProfile(session.user)
          .then((profile) => setUser(buildUser(session.user, profile)))
          .catch((err) => console.error("Profile refresh error:", err));
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    // Also check URL hash for recovery token (backup detection)
    const hash = window.location.hash;
    if (hash && hash.includes("type=recovery")) {
      setIsRecoveryMode(true);
    }

    checkAppState();

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [retryNonce]);

  const checkAppState = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const profile = await ensureProfile(session.user);
        setUser(buildUser(session.user, profile));
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setAuthTimedOut(false);
    } catch (error) {
      console.error("checkAppState error:", error);
      setAuthError({ type: 'unknown', message: error.message });
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      initialLoadDone.current = true;
      setIsLoadingAuth(false);
    }
  };

  const retryAuth = () => {
    setAuthTimedOut(false);
    setIsLoadingAuth(true);
    initialLoadDone.current = false;
    setRetryNonce((n) => n + 1);
  };

  // Slug-aware logout: if the user is currently inside an academy surface
  // (`/${slug}/...`), return them to that academy's public landing instead
  // of the platform root. Reserved top-level routes fall back to `/`.
  const resolveLogoutRedirect = () => {
    try {
      const path = window.location.pathname || "";
      const m = path.match(/^\/([^/?#]+)(?:[/?#]|$)/);
      const RESERVED = new Set([
        "instructor", "admin", "post-login", "login", "signup",
        "create-academy", "select-academy", "reset-password",
        "terms", "privacy", "editor", "meet",
        "email-unsubscribe", "unsubscribe", "academy", "pricing",
      ]);
      if (m && !RESERVED.has(m[1])) return `/${m[1]}`;
    } catch {}
    return "/";
  };

  const logout = (shouldRedirect = true) => {
    const target = resolveLogoutRedirect();
    setUser(null);
    setIsAuthenticated(false);
    setIsRecoveryMode(false);
    try {
      localStorage.removeItem("dwrrah_last_academy_slug");
    } catch {}
    supabase.auth.signOut().then(() => {
      if (shouldRedirect) {
        window.location.href = target;
      }
    });
  };

  // Slug-aware: if the user is currently inside an academy surface
  // (/${slug}/...), keep them in that academy's auth flow. Reserved
  // top-level routes (instructor/admin/etc.) fall back to platform login.
  const navigateToLogin = () => {
    try {
      const path = window.location.pathname || "";
      const m = path.match(/^\/([^/?#]+)(?:[/?#]|$)/);
      const RESERVED = new Set([
        "instructor", "admin", "post-login", "login", "signup",
        "create-academy", "select-academy", "reset-password",
        "terms", "privacy", "editor", "meet",
        "email-unsubscribe", "unsubscribe", "academy", "pricing",
      ]);
      if (m && !RESERVED.has(m[1])) {
        window.location.href = `/${m[1]}/login`;
        return;
      }
    } catch {}
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      authError,
      appPublicSettings,
      isRecoveryMode,
      authTimedOut,
      retryAuth,
      setIsRecoveryMode,
      logout,
      navigateToLogin,
      checkAppState,
      isAdminImpersonating,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
